import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
  ShiftManagementPageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import AddPlannedBreakModal from 'modules/shift_management/components/Settings/SavedShifts/AddPlannedBreakModal'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'

import { ShiftConfigWithBreaksForm } from '../forms'

export interface Props {
  form: ShiftConfigWithBreaksForm
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    form: ShiftConfigWithBreaksForm,
    isTimeFormat24H: boolean
  ) => void
  onDeleteShift: () => void
  canDelete: boolean
}

const ShiftPopover = ({ form, onAddBreak, onDeleteShift, canDelete = false }: Props) => {
  const { shiftConfig } = form
  const [openAddBreakModal, setOpenAddBreakModal] = useState<boolean>(false)
  const canUseBreaks = hasFeature('time_settings_breaks')
  const { isTimeFormat24H } = useIsTimeFormat24H()

  // Cache first to avoid fetching data on every render and policies are not updated frequently.
  const { data } = useQuery(ShiftManagementPageDocument, { fetchPolicy: 'cache-first' })
  const canAddBreaks = data?.timeSettings?._policies.manageBreakConfigurations.can

  const onClickAddBreak = () => {
    track(trackingEvents.shiftManagement.addBreakStart, { from: 'rotating-shifts' })
    setOpenAddBreakModal(true)
  }

  if (!canUseBreaks && !canDelete) {
    return null
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button type="secondary" iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end" width="s200">
          {shiftConfig.isShift && canUseBreaks && (
            <PopoverMenu.TouchableOption
              onClick={onClickAddBreak}
              title={i18n.t('shift_management.saved_shifts.actions.add.break')}
            />
          )}
          {canDelete && (
            <PopoverMenu.TouchableOption
              title={i18n.t('shift_management.rotating_shifts.delete_shift')}
              onClick={onDeleteShift}
            />
          )}
        </PopoverMenu.Content>
      </PopoverMenu>

      {openAddBreakModal && (
        <AddPlannedBreakModal
          onAddBreak={(breakConfiguration, breakType) => {
            onAddBreak(breakConfiguration, breakType, form, isTimeFormat24H)
          }}
          onClose={() => setOpenAddBreakModal(false)}
          from="rotating-shifts"
          hideCreateBreakButton={!canAddBreaks}
        />
      )}
    </>
  )
}

export default observer(ShiftPopover)
